import apiClient from '../../../utils/lib/apiClient';
import { loadingState, loadingText } from '../../../states/loadingState';
import { useRecoilValue, useSetRecoilState } from 'recoil';
import { Dirary } from '../../../types/dirary';
import { userState } from '../../../states/userState';
import { formatDate } from '../../../utils/functions/formatting';
import { useCallback } from 'react';
import { WorkerWithDiary } from '../../../types';

// 日報リスト内の同一作業者から departmentId/jobId/affiliationFactoryId を補完する
const complementWorkerFields = (diaryList: Dirary[]): Dirary[] => {
  // 作業者ごとに有効な値を収集
  const workerFieldsMap = new Map<
    string,
    { departmentId: number | null; jobId: number | null; affiliationFactoryId: number | null }
  >();

  diaryList.forEach((diary) => {
    const existing = workerFieldsMap.get(diary.workerId);
    if (!existing) {
      workerFieldsMap.set(diary.workerId, {
        departmentId: diary.departmentId || null,
        jobId: diary.jobId || null,
        affiliationFactoryId: diary.affiliationFactoryId || null,
      });
    } else {
      if (!existing.departmentId && diary.departmentId) {
        existing.departmentId = diary.departmentId;
      }
      if (!existing.jobId && diary.jobId) {
        existing.jobId = diary.jobId;
      }
      if (!existing.affiliationFactoryId && diary.affiliationFactoryId) {
        existing.affiliationFactoryId = diary.affiliationFactoryId;
      }
    }
  });

  // 補完を適用
  return diaryList.map((diary) => {
    const fields = workerFieldsMap.get(diary.workerId);
    if (!fields) return diary;

    const complemented = { ...diary };
    if (!complemented.departmentId && fields.departmentId) {
      complemented.departmentId = fields.departmentId;
    }
    if (!complemented.jobId && fields.jobId) {
      complemented.jobId = fields.jobId;
    }
    if (!complemented.affiliationFactoryId && fields.affiliationFactoryId) {
      complemented.affiliationFactoryId = fields.affiliationFactoryId;
    }
    return complemented;
  });
};

// 日報リストのバリデーション（departmentId/jobId/affiliationFactoryId が有効値かチェック）
export const validateDiaryWorkerFields = (
  diaryList: Dirary[]
): { valid: boolean; invalidWorkerNames: string[] } => {
  const invalidWorkers = new Set<string>();

  diaryList.forEach((diary) => {
    if (!diary.departmentId || !diary.jobId || !diary.affiliationFactoryId) {
      invalidWorkers.add(diary.workerName || diary.workerId);
    }
  });

  return {
    valid: invalidWorkers.size === 0,
    invalidWorkerNames: Array.from(invalidWorkers),
  };
};

// WorkerWithDiary リストのバリデーション
export const validateWorkerList = (
  workers: WorkerWithDiary[]
): { valid: boolean; invalidWorkerNames: string[] } => {
  const invalidWorkers = new Set<string>();

  workers.forEach((worker) => {
    if (
      worker.departmentId === null ||
      worker.departmentId === 0 ||
      worker.jobId === null ||
      worker.jobId === 0 ||
      !worker.place ||
      worker.place === '0'
    ) {
      invalidWorkers.add(worker.workerName || worker.workerId);
    }
  });

  return {
    valid: invalidWorkers.size === 0,
    invalidWorkerNames: Array.from(invalidWorkers),
  };
};

const createAllDiaryList = (
  workers: WorkerWithDiary[],
  diaries: Dirary[]
): Dirary[] => {
  const allDiaryList: Dirary[] = [];

  workers.forEach((worker) => {
    diaries.forEach((diary) => {
      const newDiary = { ...diary }; // diaryをコピーして新しいオブジェクトを作成

      // workerListにある項目で上書き
      newDiary.workerId = worker.workerId;
      newDiary.workerName = worker.workerName;
      if (worker.departmentId !== null) {
        newDiary.departmentId = worker.departmentId;
      }
      if (worker.jobId !== null) {
        newDiary.jobId = worker.jobId;
      }
      if (worker.place) {
        newDiary.affiliationFactoryId = Number(worker.place);
      }

      allDiaryList.push(newDiary);
    });
  });

  // 同一作業者の日報から不足フィールドを補完
  return complementWorkerFields(allDiaryList);
};

export const useAddPosts = () => {
  const setLoadingAnimation = useSetRecoilState(loadingState);
  const setLoadingText = useSetRecoilState(loadingText);
  const user = useRecoilValue(userState);
  const addPosts = useCallback(
    async (diaryList: Dirary[]) => {
      // 同一作業者の日報から不足フィールドを補完
      const complementedList = complementWorkerFields(diaryList);

      setLoadingAnimation(true);
      setLoadingText('登録中...');
      const createdAt = formatDate(new Date());
      const reqBody = {
        diaryList: complementedList,
      };
      try {
        const res = await apiClient.post(
          `/siteDiary/posts/addDiary/${
            user.mail ?? 'undefined@morimatsu.jp'
          }/${createdAt}`,
          reqBody
        );

        const data = res.data;
        return data;
        //   return 0;
      } catch (e) {
        window.alert('エラーが発生しました' + e);
        return null;
      } finally {
        setLoadingAnimation(false);
        setLoadingText('');
      }
    },
    [loadingState, loadingText]
  );
  const addPostsAllWorkers = useCallback(
    async (diaryList: Dirary[], workerList: WorkerWithDiary[]) => {
      const allDiaryList = createAllDiaryList(workerList, diaryList);
      await addPosts(allDiaryList);
    },
    [loadingState, loadingText]
  );
  return { addPosts, addPostsAllWorkers };
};
